import { db, auth, storage } from '../firebaseConfig';
import { collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, deleteUser, updateProfile } from 'firebase/auth';
import { ref, deleteObject } from 'firebase/storage';
import { requestChangeEmailVerification } from './emailActionsService';
import { friendlyAuthError } from '../utils/friendlyAuthError';

// Collections that carry an ownerUserId field scoped to the Manager account,
// cleaned up in full on account deletion. `feedback` is deliberately excluded
// -- it's a create-only mailbox with no read/delete rule for the client (see
// firestore.rules), so past feedback can't be queried/removed this way; those
// stray docs are harmless once no live account references the uid.
const OWNER_SCOPED_COLLECTIONS = ["logs", "profiles", "routines", "exercises"];

const deleteOwnerScopedDocs = async (uid) => {
  for (const collectionName of OWNER_SCOPED_COLLECTIONS) {
    const q = query(collection(db, collectionName), where("ownerUserId", "==", uid));
    const snapshot = await getDocs(q);
    await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
  }
};

// Reads profile photoPaths before their docs are deleted above -- Storage
// objects are independent of the Firestore docs that reference them, so this
// must run first. Individual failures are swallowed (matches
// profilesService.js's own best-effort old-photo cleanup) since a stray
// Storage object is far less important than the account deletion completing.
const deleteProfilePhotos = async (uid) => {
  const q = query(collection(db, "profiles"), where("ownerUserId", "==", uid));
  const snapshot = await getDocs(q);
  const paths = snapshot.docs.map((d) => d.data().photoPath).filter(Boolean);
  await Promise.all(paths.map((path) => deleteObject(ref(storage, path)).catch(() => {})));
};

// Permanently deletes the signed-in user's account and all of their data --
// Hero profiles (+ uploaded photos), routines, logged workouts, and custom
// exercises -- then the Firebase Auth account itself. Requires the current
// password: Firebase requires a "recent login" for account deletion, and
// re-confirming the password doubles as a safety check before an
// irreversible action. onAuthStateChanged in App.js picks up the signed-out
// state automatically once this resolves -- no manual navigation needed.
export const deleteAccount = async (password) => {
  const user = auth.currentUser;
  if (!user) return { success: false, error: "You need to be logged in to do that." };

  try {
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
  } catch (error) {
    return { success: false, error: friendlyAuthError(error) };
  }

  try {
    await deleteProfilePhotos(user.uid);
    await deleteOwnerScopedDocs(user.uid);
    await deleteUser(user);
    return { success: true };
  } catch (error) {
    return { success: false, error: friendlyAuthError(error) };
  }
};

// Changes the signed-in user's login email -- this IS the account's identity
// for both Firebase Auth sign-in and the Contact form's Reply-To (Contact.jsx
// reads auth.currentUser.email live at submit time, so there's no separate
// "contact email" field to keep in sync). Requires the current password for
// the same "recent login" reason as deleteAccount above. The confirmation
// link only takes effect once clicked, so a typo in the new address can't
// lock the user out -- same guarantee the client SDK's verifyBeforeUpdateEmail
// provided; this now routes through a Cloud Function instead (see
// emailActionsService.js) so the email comes from info@pumpous.com rather
// than Firebase's generic shared sender, which was landing in spam.
export const changeEmail = async (newEmail, password) => {
  const user = auth.currentUser;
  if (!user) return { success: false, error: "You need to be logged in to do that." };

  try {
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
  } catch (error) {
    return { success: false, error: friendlyAuthError(error) };
  }

  return requestChangeEmailVerification(newEmail);
};

// Changes the account's username -- Firebase Auth's `displayName`, the same
// field the signup form and UsernameGate write to, and the one App.js reads
// as `displayName` for the "Welcome, {name}" heading. Unlike changeEmail,
// this touches no sign-in credential, so it needs no reauthentication/password.
export const changeUsername = async (newUsername) => {
  const user = auth.currentUser;
  if (!user) return { success: false, error: "You need to be logged in to do that." };

  try {
    await updateProfile(user, { displayName: newUsername });
    return { success: true };
  } catch (error) {
    return { success: false, error: friendlyAuthError(error) };
  }
};
